import { stripMarkdownToPlainText } from '@vrzl/ui/markdownPlainText';

import { answerSources, type AnswerSource } from '@/domains/overview/utils/sources';

import { evidenceSourceMatchesDocumentUrl } from '../api/evidenceSourceMatch';
import type { ValidationAnswer, ValidationReportItem } from '../api/types';

import { criterionStatus } from './validationView';

export const MISSING_SUMMARY = 'Niet gevonden in de documenten';

export type Finding = {
  /** Gevonden zin als platte tekst; zonder citaat de waarde zelf. */
  text: string;
  /** Undefined als de bron niet bij een document van het evenement hoort. */
  source: AnswerSource | undefined;
  /** Waarde die de documentweergave markeert. */
  highlight: string | undefined;
};

/** Vondsten van één antwoord: één per citaat, in volgorde. */
export function answerFindings(
  answer: ValidationAnswer,
  documents: ValidationReportItem['documents'],
): Finding[] {
  const highlight = answer.value ?? undefined;
  const quotes = answer.evidence.flatMap((ev): Finding[] => {
    const text =
      ev.plainSentence?.trim() || (ev.sentence ? stripMarkdownToPlainText(ev.sentence) : '');
    if (!text) return [];
    const doc = documents.find((d) => evidenceSourceMatchesDocumentUrl(ev.source, d.url));
    const source = doc && {
      docId: doc.id,
      docName: doc.name,
      pages: [...new Set(ev.pages ?? [])].sort((a, b) => a - b),
      evidence: ev,
    };
    return [{ text, source, highlight }];
  });
  if (quotes.length > 0) return quotes;
  if (!answer.value) return [];
  return [{ text: answer.value, source: answerSources(answer, documents)[0], highlight }];
}

export const criterionFindings = (item: ValidationReportItem): Finding[] =>
  item.answers.flatMap((answer) => answerFindings(answer, item.documents));

/** Eén regel voor de dichtgeklapte rij. */
export function criterionSummary(item: ValidationReportItem): string {
  if (criterionStatus(item) === 'missing') return MISSING_SUMMARY;
  const values = [
    ...new Set(item.answers.map((a) => a.value?.trim()).filter((v): v is string => !!v)),
  ];
  if (values.length > 0) return values.join(' · ');
  return criterionFindings(item)[0]?.text ?? '';
}
